import React, { useEffect, useReducer, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import moment from 'moment'

import TipDialogueBox from 'components/Shared/TipDialogueBox'
import { SecondaryButton, TransparentBgButton } from 'components/Shared/Button'
import { numberWithThousandsSeparators, getAdSuggestions } from 'utils/helpers'
import AdsSummaryItem from './AdsSummaryItem'
import { initStateObject, objectReducer } from 'reducers/default'
import { fetchPageInfos } from 'api/integrations'
import { AssetClass, Platform } from 'api/models'

const getVideoDimensions = (url) => {
    return new Promise(resolve => {
        // create the video element
        const video = document.createElement('video')

        // place a listener on it
        video.addEventListener( 'loadedmetadata', function () {
            // retrieve dimensions
            const height = this.videoHeight
            const width = this.videoWidth

            // send back result
            resolve({ width, height })
        }, false)

        // start download meta-datas
        video.src = url
    })
}

const getImageDimensions = (url) => {
    return new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => resolve({ width: img.width, height: img.height })
        img.onerror = (err) => reject(err)
        img.src = url
    })
}

const getMediaDimensions = async (mediaFile) => {
    let dimensions = {}
    if(mediaFile?.type.includes('video')) {
        dimensions = await getVideoDimensions(mediaFile?.url)
    } else {
        dimensions = await getImageDimensions(mediaFile?.url)
    }

    return dimensions
}

const SummaryItem = ({ title, value }) => {
    return <div className='card card-no-padding-mobile rounded flex-row'>
        <div className="summary_item_icon">
            <i className="icon-campaign" />
        </div>
        <p className="summary_item_content">
            <strong>
                {value}
            </strong>
            <span>
                {title}
            </span>
        </p>
    </div>
}

SummaryItem.propTypes = {
    title: PropTypes.string.isRequired,
    value: PropTypes.string.isRequired
}

const Summary = ({ campaigns, totalBudget, creatives, runtimeStartDate, runtimeEndDate, onBack, boost, setShowCancelSetupModal, canShowSummary, scrollPosition, integrationDetails }) => {
    const { t } = useTranslation()
    const [adSuggestions, setAdSuggestions] = useState({})
    const [totalAds, setTotalAds] = useState(0)
    const [fbPageInfo, setFbPageInfo] = useReducer(objectReducer, initStateObject)

    const getRuntime = () => {
        return `${new moment(runtimeStartDate).format('DD.MM.YYYY')} - ${moment(runtimeEndDate).format('DD.MM.YYYY')}`
    }

    useEffect(() => {
        const getInfo = async () => {
            const meta_profile = integrationDetails.find(integration_detail => integration_detail.asset_class === AssetClass.profile && integration_detail.platform === Platform.facebook)
            await fetchPageInfos(setFbPageInfo, meta_profile?.asset_id)
        }

        getInfo()
    }, [integrationDetails])

    useEffect(() => {
        const constructCreative = async () => {
            if(creatives?.length) {
                const newCreatives = await Promise.all(creatives.map(async function(creative) {
                    const media = await Promise.all(creative.media.map(async function(media){
                        const mediaDimensions = await getMediaDimensions(media)
                        return { 
                            ...media,
                            height: mediaDimensions.height,
                            width: mediaDimensions.width,
                            ratio: mediaDimensions.height/mediaDimensions.width
                        }
                    }))
    
                    return {
                        ...creative,
                        media
                    }
                }))

                const [regular, reels] = getAdSuggestions(newCreatives, [fbPageInfo], t)
                setAdSuggestions({ regular, reels })
                setTotalAds(regular.length + reels.length)
            }
        }
        constructCreative()

    }, [creatives, fbPageInfo, t])
    
    const currency = campaigns.items?.length > 0 ? campaigns.items[0].currency : ''
    const userCurrency = localStorage.getItem('userCurrency') || currency || ''

    return <div id="summary">
        <TipDialogueBox 
            title={t('Recommendations.summary.title')} 
            text={t('Recommendations.summaryTextTBD')}
        />

        <div className='accordion mt-4'>
            <SummaryItem 
                title={t('Recommendations.summary.budget')}
                value={`${totalBudget ? numberWithThousandsSeparators(totalBudget) : 'error'} ${userCurrency}`}
            />
            <SummaryItem 
                title={t('Recommendations.summary.totalTasteClusters')}
                value={`${campaigns.length}`}
            />
            <SummaryItem 
                title={t('Recommendations.summary.runtime')}
                value={getRuntime()}
            />
            <AdsSummaryItem 
                title={t('Recommendations.summary.totalAds')}
                value={`${totalAds}`}
                adSuggestions={adSuggestions}
            /> 
        </div>

        <div
            className={`recommendation-setup-action-buttons d-flex justify-content-between align-items-center ${scrollPosition > 0 ? 'scroll-shadow': ''}`}
        >
            <div className='d-flex justify-content-between w-100'>
                <TransparentBgButton
                    className='bt'
                    onClick={() => onBack()}
                    text={t('Recommendations.btnBack')}
                    fullWidth={false}
                    color='navy'
                />
                <div className='d-flex gap-3'>
                    <TransparentBgButton
                        text={t('Recommendations.btnCancelSetup')}
                        onClick={() => setShowCancelSetupModal(true)}
                        color='navy'
                    />
                    <SecondaryButton
                        disabled={!canShowSummary}
                        className='bt'
                        onClick={boost}
                        text={t('Recommendations.summary.publish')}
                        fullWidth={false}
                    />
                </div>
            </div>
        </div>
    </div>
}

Summary.propTypes = {
    campaigns: PropTypes.array.isRequired,
    onBack: PropTypes.func.isRequired,
    boost: PropTypes.func.isRequired,
    setShowCancelSetupModal: PropTypes.func.isRequired,
    canShowSummary: PropTypes.bool.isRequired,
    scrollPosition: PropTypes.number,
    totalBudget: PropTypes.number,
    creatives: PropTypes.array,
    runtimeStartDate: PropTypes.string, 
    runtimeEndDate: PropTypes.string,
    integrationDetails: PropTypes.arrayOf(Object)
}

export default Summary